import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Avatar, CircularProgress, Divider,
} from '@mui/material';
import { Visibility as ViewIcon, School as SchoolIcon, Print as PrintIcon, FileDownload as DownloadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { ratingAPI } from '../../services/api';
import { errorText } from '../../utils/format';
import { fmtDate } from '../../utils/dates';
import { printPage, esc } from '../../utils/print';
import { ScoreView, CriterionBars, Distribution, toScale, isStars, scaleMax } from './shared';

export default function ResultsTab({ config, settings, notify }) {
  const scaleType = config?.scaleType || 'STARS_5';
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const classes = [...new Map(settings.map((s) => [s.classId, s.className])).entries()].map(([id, name]) => ({ id, name }));
  const batches = settings.filter((s) => String(s.classId) === String(classId));

  useEffect(() => { setSectionId(''); }, [classId]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await ratingAPI.getDashboard({ classId: classId || undefined, sectionId: sectionId || undefined })).data); }
    catch (e) { notify(errorText(e, 'Could not load feedback results'), 'error'); }
    setLoading(false);
  }, [classId, sectionId, notify]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (t) => {
    setDetail({ loading: true, teacherName: t.teacherName });
    setDetailLoading(true);
    try { setDetail((await ratingAPI.getTeacherDetail(t.teacherId, { classId: classId || undefined, sectionId: sectionId || undefined })).data); }
    catch (e) { notify(errorText(e, 'Could not load details'), 'error'); setDetail(null); }
    setDetailLoading(false);
  };

  const scopeText = () => {
    const c = classes.find((x) => String(x.id) === String(classId))?.name;
    const b = batches.find((x) => String(x.sectionId) === String(sectionId))?.sectionName;
    return c ? `${c}${b ? ' - ' + b : ''}` : 'All programs';
  };

  const exportExcel = () => {
    const names = data.criteriaNames || [];
    const sheet = data.teachers.map((t) => {
      const row = { Faculty: t.teacherName, Designation: t.designation || '', 'Ratings received': t.totalRatings, [`Overall (${isStars(scaleType) ? 'out of 5' : 'out of 10'})`]: toScale(t.avg10, scaleType) };
      names.forEach((n) => { const c = t.criteria.find((x) => x.name === n); row[n] = c ? toScale(c.avg10, scaleType) : ''; });
      return row;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'Faculty feedback');
    XLSX.writeFile(wb, 'faculty_feedback.xlsx');
  };

  const printDetail = () => {
    const d = detail;
    const body = `<div class="grid"><div class="card"><div class="v">${toScale(d.avg10, scaleType).toFixed(1)}</div><div class="l">Overall (${isStars(scaleType) ? 'out of 5' : 'out of 10'})</div></div>
      <div class="card"><div class="v">${d.totalRatings}</div><div class="l">Feedback received</div></div></div>
      <h3>Score by question</h3><table><tbody>${d.criteria.map((c) => `<tr><td>${esc(c.name)}</td><td class="r b">${toScale(c.avg10, scaleType).toFixed(1)}</td></tr>`).join('')}</tbody></table>
      <h3>By program and batch</h3><table><thead><tr><th>Batch</th><th class="r">Responses</th><th class="r">Average</th></tr></thead><tbody>${d.byBatch.map((b) => `<tr><td>${esc(b.batch)}</td><td class="r">${b.count}</td><td class="r b">${toScale(b.avg10, scaleType).toFixed(1)}</td></tr>`).join('')}</tbody></table>
      <h3>Comments</h3><table><tbody>${d.rows.filter((r) => r.comment).map((r) => `<tr><td>${esc(r.className)} - ${esc(r.sectionName)}</td><td>${esc(r.comment)}</td></tr>`).join('') || '<tr><td>No comments</td></tr>'}</tbody></table>`;
    printPage('Faculty Feedback Report', `${d.teacherName} - ${scopeText()}`, body);
  };

  const teachers = data?.teachers || [];
  const criteriaNames = data?.criteriaNames || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Program / year" value={classId} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 240 }}>
            <MenuItem value="">All programs</MenuItem>
            {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Batch" value={sectionId} onChange={(e) => setSectionId(e.target.value)} sx={{ minWidth: 150 }} disabled={!classId}>
            <MenuItem value="">All batches</MenuItem>
            {batches.map((b) => <MenuItem key={b.sectionId} value={b.sectionId}>{b.sectionName}</MenuItem>)}
          </TextField>
        </Box>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!teachers.length}>Export Excel</Button>
      </Box>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : data && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead><TableRow>
                    <TableCell>Faculty</TableCell><TableCell>Overall</TableCell><TableCell align="center">Responses</TableCell><TableCell sx={{ minWidth: 280 }}>Score by question</TableCell><TableCell align="right">Details</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {teachers.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>No feedback has been given for {scopeText().toLowerCase()} yet</TableCell></TableRow>
                    ) : teachers.map((t) => (
                      <TableRow key={t.teacherId} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: '#e0f2f1', color: '#00796b', width: 36, height: 36 }}><SchoolIcon fontSize="small" /></Avatar>
                            <Box><Typography sx={{ fontWeight: 700 }}>{t.teacherName}</Typography><Typography variant="caption" color="text.secondary">{t.designation}</Typography></Box>
                          </Box>
                        </TableCell>
                        <TableCell><ScoreView avg10={t.avg10} scaleType={scaleType} /></TableCell>
                        <TableCell align="center"><Chip size="small" label={t.totalRatings} /></TableCell>
                        <TableCell><CriterionBars criteria={t.criteria} scaleType={scaleType} dense /></TableCell>
                        <TableCell align="right"><Tooltip title="Full breakdown"><IconButton onClick={() => openDetail(t)}><ViewIcon /></IconButton></Tooltip></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Where the college stands</Typography>
              <Typography variant="caption" color="text.secondary">Average score for each question, {scopeText().toLowerCase()}</Typography>
              <Box sx={{ mt: 2 }}>
                {criteriaNames.length === 0 ? <Typography variant="body2" color="text.secondary">No scores yet.</Typography> : <CriterionBars criteria={data.criteriaOverall} scaleType={scaleType} />}
              </Box>
            </Paper>
            <Paper sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>How feedback is spread</Typography>
              <Distribution counts={data.starDistribution} />
              <Typography variant="caption" color="text.secondary">Number of feedback forms by overall star rating (1 to 5 stars; a 1-10 score counts as score ÷ 2).</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{detail?.teacherName}{detail?.designation ? ` · ${detail.designation}` : ''}</span>
          {detail && !detail.loading && <Chip size="small" label={scopeText()} />}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail || detail.loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Overall</Typography>
                    <Box sx={{ my: 1 }}><ScoreView avg10={detail.avg10} scaleType={scaleType} size={26} showBoth /></Box>
                    <Typography variant="body2" color="text.secondary">{detail.totalRatings} feedback form(s)</Typography>
                    <Divider sx={{ my: 2 }} />
                    <Distribution counts={detail.starDistribution} />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Score by question (out of {scaleMax(scaleType)})</Typography>
                    <CriterionBars criteria={detail.criteria} scaleType={scaleType} />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ p: 2 }}>Class-wise (program and batch)</Typography>
                    <Table size="small"><TableHead><TableRow><TableCell>Batch</TableCell><TableCell align="right">Responses</TableCell><TableCell align="right">Average</TableCell></TableRow></TableHead>
                      <TableBody>{detail.byBatch.map((b) => (
                        <TableRow key={b.batch}><TableCell>{b.batch}</TableCell><TableCell align="right">{b.count}</TableCell><TableCell align="right"><ScoreView avg10={b.avg10} scaleType={scaleType} size={14} /></TableCell></TableRow>
                      ))}</TableBody>
                    </Table>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ p: 2 }}>Subject-wise</Typography>
                    <Table size="small"><TableHead><TableRow><TableCell>Subject</TableCell><TableCell align="right">Responses</TableCell><TableCell align="right">Average</TableCell></TableRow></TableHead>
                      <TableBody>{detail.bySubject.map((b) => (
                        <TableRow key={b.subject}><TableCell>{b.subject}</TableCell><TableCell align="right">{b.count}</TableCell><TableCell align="right"><ScoreView avg10={b.avg10} scaleType={scaleType} size={14} /></TableCell></TableRow>
                      ))}</TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Individual feedback {detail.anonymous && <Chip size="small" label="Anonymous mode: names hidden" sx={{ ml: 1 }} />}</Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>Student</TableCell><TableCell>Batch</TableCell><TableCell>Subject</TableCell><TableCell>Overall</TableCell><TableCell>Scores</TableCell><TableCell>Comment</TableCell><TableCell>Date</TableCell></TableRow></TableHead>
                    <TableBody>
                      {detail.rows.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.studentName}{r.admissionNo && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.admissionNo}</Typography>}</TableCell>
                          <TableCell>{r.className} - {r.sectionName}</TableCell>
                          <TableCell>{r.subjectName || '-'}</TableCell>
                          <TableCell><ScoreView avg10={r.overall10} scaleType={scaleType} size={14} /></TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {r.scores.map((s) => <Tooltip key={s.name} title={s.name}><Chip size="small" label={`${s.score}/${s.scaleMax}`} variant="outlined" /></Tooltip>)}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 240 }}>{r.comment || '-'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                      {detail.rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No feedback yet</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detail && !detail.loading && <Button startIcon={<PrintIcon />} onClick={printDetail}>Print report</Button>}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
